import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ContentCard from "@/components/base/ContentCard";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { categoryMeta } from "@/mocks/content";
import type { ContentItem, ContentType } from "@/types/content";
import { getByType, getFilterOptions, paginate, totalPages } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilterSidebar from "@/pages/category/components/FilterSidebar";

const PAGE_SIZE = 6;
const validTypes: ContentType[] = ["plot", "character", "news"];

export default function Category() {
  const { type = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const contentType = (validTypes.includes(type as ContentType) ? type : "") as ContentType | "";
  const meta = categoryMeta.find((c) => c.type === contentType);

  const filter = (() => {
    const keys = getFilterOptions((contentType || "plot") as ContentType).map((o) => o.key);
    const raw = searchParams.get("f") || "all";
    return keys.includes(raw) ? raw : "all";
  })();
  const sort = searchParams.get("sort") || "latest";
  const page = Math.max(1, Number(searchParams.get("page") || "1"));

  usePageMeta(
    meta ? `${meta.name} - 年轻的母亲3` : "年轻的母亲3",
    meta ? `${meta.name}：${meta.desc}` : undefined,
  );

  // Reset filters when switching category type
  useEffect(() => {
    setSearchParams({}, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType]);

  const items = useMemo((): ContentItem[] => {
    if (!contentType) return [];
    let list = getByType(contentType);

    list = list.filter((item) => {
      if (filter === "all") return true;
      if (contentType === "plot") {
        const [a, b] = filter.split("-").map(Number);
        return (item.chapter ?? 0) >= a && (item.chapter ?? 0) <= b;
      }
      if (contentType === "character") return item.role === filter;
      return item.date.startsWith(filter);
    });

    if (sort === "latest") {
      list = [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
    }
    return list;
  }, [contentType, filter, sort]);

  const pages = totalPages(items.length, PAGE_SIZE);
  const currentPage = Math.min(page, pages);
  const pageItems = paginate(items, currentPage, PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all" || !value) next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setSearchParams({}, { replace: true });
  };

  if (!contentType || !meta) {
    return (
      <div className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto">
          <EmptyState
            title="该分类不存在"
            desc="你访问的分类页面不存在或已调整，返回首页浏览《年轻的母亲3》的其他内容吧。"
            to="/"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-[1200px] mx-auto">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: meta.name }]} />

        <header className="mt-5 mb-8 md:mb-10">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
              <i className={`${meta.icon} text-2xl`}></i>
            </span>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              {meta.name}
              <span className="text-foreground-400 font-normal text-xl md:text-2xl"> · 年轻的母亲3</span>
            </h1>
          </div>
          <p className="mt-4 text-sm md:text-[15px] text-foreground-600 leading-relaxed max-w-3xl">
            {meta.desc}
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <FilterSidebar
                type={contentType}
                activeFilter={filter}
                onFilterChange={(key) => updateParam("f", key)}
                sort={sort}
                onSortChange={(key) => updateParam("sort", key)}
              />
            </div>
          </aside>

          <div className="min-w-0">
            <div className="lg:hidden mb-4">
              <button
                type="button"
                onClick={() => setMobileFilterOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-md border border-background-200 bg-background-50 text-sm font-medium text-foreground-800 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-600"><i className="ri-filter-3-line"></i></span>
                  筛选与排序
                </span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform ${mobileFilterOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-lg"></i>
                </span>
              </button>
              {mobileFilterOpen && (
                <div className="mt-3 animate-fade-in">
                  <FilterSidebar
                    type={contentType}
                    activeFilter={filter}
                    onFilterChange={(key) => updateParam("f", key)}
                    sort={sort}
                    onSortChange={(key) => updateParam("sort", key)}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-foreground-500">
                共 <span className="text-foreground-900 font-medium">{items.length}</span> 条内容
              </p>
              <p className="text-sm text-foreground-500 flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-sort-desc"></i></span>
                {sort === "latest" ? "最新发布" : "默认排序"}
              </p>
            </div>

            {pageItems.length === 0 ? (
              <EmptyState
                title="暂无相关内容"
                desc="当前筛选条件下没有匹配的内容，试试重置筛选条件，查看《年轻的母亲3》的全部内容。"
                actionLabel="重置筛选"
                onAction={resetFilters}
              />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {pageItems.map((item) => (
                    <div key={item.id} className="animate-soft-in">
                      <ContentCard item={item} />
                    </div>
                  ))}
                </div>
                <Pagination
                  page={currentPage}
                  totalPages={pages}
                  onChange={(p) => {
                    updateParam("page", String(p));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
